"use client";
import AlertDialog from "@/components/primitives/alert-dialog";
import Button from "@/components/primitives/button";
import Icon from "@/components/primitives/icon";
import { useBettor } from "@/utils/providers/bettor";
import {
  BettorActionPayload,
  BettorActionType,
  BettorBasic,
  BettorPicksMetaData,
  DEFAULT_SMALL_INT_FLAGS,
  disableFlag,
  enableFlag,
  hasAnyFlag,
  isFlagEnabled,
  MoneylineAction2,
  NotificationChannel,
  Result,
} from "common";
import { useAuth } from "@clerk/clerk-react";

import React, { useContext } from "react";
import mlClient from "@/ml-client";
import Modal from "@/components/primitives/modal";
import { LoadingCircle } from "@/components/primitives/loading";
import assert from "assert";
import { useRoom } from "@/utils/providers/room";
import Form from "@/components/primitives/form";
import Switch from "@/components/primitives/switch";
import BettorDisplay from "./bettor-display";
import { AuthContext } from "@/app/(public)/drawer";
import { useCooldown } from "@/utils/providers/cooldown";

export type BettorRelationshipActionsType =
  | "account"
  | "target-account"
  | "target-profile";

export type BettorRelationshipProps = {
  bettor: BettorBasic & BettorPicksMetaData;
  high_voltage: boolean;
  relationship_notification_flags?: number;
  relationship_notification_channel_flags?: number;
  following: boolean;
  disabled: boolean;
  shareable?: string;
};

type NotificationChannelModel = {
  type: NotificationChannel;
  name: string;
  description: string;
  icon: string;
};

const NOTIFICATION_CHANNELS: NotificationChannelModel[] = [
  {
    type: NotificationChannel.Email,
    name: "Email",
    description:
      "Receive an email alert to the address associated with the account.",
    icon: "mail",
  },
  {
    type: NotificationChannel.SMS,
    name: "SMS",
    description: "Receive a text message alert. Standard data rates apply.",
    icon: "telegram",
  },
  {
    type: NotificationChannel.Telegram,
    name: "Telegram",
    description: "Receive a telegram message alert for the connected account.",
    icon: "telegram",
  },
  {
    type: NotificationChannel.Discord,
    name: "Discord",
    description: "Receive a discord message alert for the connected account.",
    icon: "discord",
  },
];

export type BettorRelationshipActionsContextType = {
  type: BettorRelationshipActionsType;
  target?: BettorRelationshipProps;
  highVoltage: boolean;
  setHighVoltage:React.Dispatch<React.SetStateAction<boolean>>;
  targetList?: BettorRelationshipProps[];
  managingTarget: boolean;
  setManagingTarget: React.Dispatch<React.SetStateAction<boolean>>;
  targetNotificationChannelFlags: number | null;
  setTargetNotificationChannelFlags: React.Dispatch<
    React.SetStateAction<number | null>
  >;
  connectingAccount: NotificationChannelModel | null;
  setConnectingAccount: React.Dispatch<
    React.SetStateAction<NotificationChannelModel | null>
  >;
  notificationChannels: Partial<Record<NotificationChannel, string>> | null;
  setNotificationChannels: React.Dispatch<
    React.SetStateAction<{ [key in NotificationChannel]?: string } | null>
  >;
};

export const BettorRelationshipActionsContext =
  React.createContext<BettorRelationshipActionsContextType>({
    type: "account",
    target: undefined,
    highVoltage: false,
    setHighVoltage: () => {},
    targetList: undefined,
    managingTarget: false,
    setManagingTarget: () => {},
    targetNotificationChannelFlags: null,
    setTargetNotificationChannelFlags: () => {},
    connectingAccount: null,
    setConnectingAccount: () => {},
    notificationChannels: null,
    setNotificationChannels: () => {},
  });

export const useBettorRelationshipActions = () => {
  const context = React.useContext(BettorRelationshipActionsContext);
  if (!context) {
    throw new Error(
      "useBettorRelationshipActionsContext must be used within an AccountNotificationSettingsProvider"
    );
  }
  return context;
};

export type BettorRelationshipActionsProps = {
  type: BettorRelationshipActionsType;
  target?: BettorRelationshipProps;
  targetList?: BettorRelationshipProps[];
  children?: React.ReactNode;
};

export default function BettorRelationshipActions(
  props: BettorRelationshipActionsProps
) {
  const { bettor } = useBettor();
  const wsData = useRoom();
  const [connectingAccount, setConnectingAccount] =
    React.useState<NotificationChannelModel | null>(null);
  const [managingTarget, setManagingTarget] = React.useState<boolean>(false);
  const [highVoltage, setHighVoltage] =
  React.useState<boolean>(props.target ? props.target.high_voltage : false);
  const [targetNotificationChannelFlags, setTargetNotificationChannelFlags] =
    React.useState<number | null>(
      props.target?.relationship_notification_channel_flags ?? null
    );
  const [notificationChannels, setNotificationChannels] = React.useState<
    { [key in NotificationChannel]?: string } | null
  >(bettor?.details.notification_channels ?? null);

  React.useEffect(() => {
    if (!wsData.roomData) return;
    if (wsData.roomData.type !== "connected_account") return;
    console.log(wsData.roomData);
    const updatedNotificationChannel: {
      notification_channel_type: NotificationChannel;
      notification_channel: string;
    } = wsData.roomData.data;
    setNotificationChannels({
      ...(notificationChannels ?? {}),
      [updatedNotificationChannel.notification_channel_type]:
        updatedNotificationChannel.notification_channel,
    });
  }, [wsData.roomData]);

  return (
    <BettorRelationshipActionsContext.Provider
      value={{
        type: props.type,
        target: props.target,
        highVoltage,
        setHighVoltage,
        targetList: props.targetList,
        managingTarget,
        setManagingTarget,
        targetNotificationChannelFlags,
        setTargetNotificationChannelFlags,
        connectingAccount,
        setConnectingAccount,
        notificationChannels,
        setNotificationChannels,
      }}
    >
      {props.children}
      {/* {props.type === "account" ? (
        <>
          <ChannelList />
        </>
      ) : props.type === "target-profile" && props.target !== undefined ? (
        <FollowActions />
      ) : props.type === "target-account" ? (
        <TargetList />
      ) : undefined} */}
    </BettorRelationshipActionsContext.Provider>
  );
}

function ManageTarget() {
  const { target, managingTarget, setManagingTarget, notificationChannels, highVoltage, setHighVoltage } =
    useBettorRelationshipActions();
    const { isSignedIn, getToken } = useAuth();
    const { setAuth } = useContext(AuthContext);

    assert(target);
    

    const handleBettorRelationshipAction = async (
      action: BettorActionType
    ): Promise<void> => {
      if (!isSignedIn) {
        setAuth("register");
        return;
      }
      try {
        const token = await getToken();
        const mlAction: MoneylineAction2<BettorActionType, BettorActionPayload> =
          {
            type: action,
            data: { target: target.bettor.id },
          };
        const response = await mlClient.post("bettors", mlAction, token);
        if (!response.ok) {
          throw new Error(response.message);
        }
        switch (action) {
          case BettorActionType.HighVoltageNotificationEnable:
            setHighVoltage(true);
            break;
          case BettorActionType.HighVoltageNotificationDisable:
            setHighVoltage(false);
            break;
          default:
            const message = `Invalid bettor relationship action: ${action}`;
            console.error(message);
        }
      } catch (err: any) {
        console.error("Error in handleBettorRelationshipAction", err);
      }
    };
  assert(target);
  return (
    <Modal open={managingTarget}>
      <Modal.Header
        onClose={() => setManagingTarget(false)}
        title={
          <div className="flex flex-row items-center gap-4">
            <span>Manage Alerts</span>
          </div>
        }
      />
      <div className="flex flex-col p-4 px-6 pt-0 md:px-12 md:pt-2 md:pb-10 w-full gap-6 md:gap-8 md:w-[760px]">
        <div className="flex flex-row p-4 md:p-6 rounded-xl gap-3 md:gap-4 items-center justify-between bg-surface-600">
          <h4 className="text-headline-medium text-secondary-300">
            <div className="flex flex-row gap-4">
              <span>Enable High Voltage Alerts for</span>
              <BettorDisplay
                bettor={target.bettor}
                disableLastPicks
                noLink
                noCard
              />
            </div>
          </h4>
          <Switch
            id="switchAll"
            checked={highVoltage}
            onCheckedChange={(b) => handleBettorRelationshipAction(b === true ? BettorActionType.HighVoltageNotificationEnable : BettorActionType.HighVoltageNotificationDisable)}
          />
        </div>
        <ChannelList disabled={!highVoltage}/>
      </div>

      <div className="flex flex-wrap items-center justify-between p-6 pt-3 w-full gap-4">
        <h4 className="text-body-regular text-secondary-400">
          {!notificationChannels ? 0 : Object.keys(notificationChannels).length}{" "}
          connections
        </h4>
        <Button
          color="primary"
          size="md"
          variant="standard"
          onClick={() => setManagingTarget(false)}
        >
          Done
        </Button>
      </div>
    </Modal>
  );
}

export function TargetList({ data }: { data: BettorRelationshipProps[] }) {
  const { getToken } = useAuth();

  const [list, setList] = React.useState<BettorRelationshipProps[]>(data || []);

  const removeHighVoltageTarget = async (i: number): Promise<void> => {
    const target = list[i];
    try {
      const token = await getToken();
      const mlAction: MoneylineAction2<BettorActionType, BettorActionPayload> =
        {
          type: BettorActionType.HighVoltageNotificationDisable,
          data: { target: target.bettor.id },
        };
      const response = await mlClient.post("bettors", mlAction, token);
      if (!response.ok) {
        throw new Error(response.message);
      }
      setList((prevList) => prevList.filter((_, index) => index !== i));
    } catch (err: any) {
      console.error("Error in removeHighVoltageTarget", err);
    }
  };

  return (
    <>
      {list.map((t, i) => (
        <BettorRelationshipActions key={i} type="target-account" target={t}>
          <TargetRow i={i} removeHighVoltageTarget={removeHighVoltageTarget} />
        </BettorRelationshipActions>
      ))}
    </>
  );
}

function TargetRow({
  i,
  removeHighVoltageTarget,
}: {
  i: number;
  removeHighVoltageTarget: (i: number) => void;
}) {
  const {
    target,
    notificationChannels,
    targetNotificationChannelFlags,
    setManagingTarget,
  } = useBettorRelationshipActions();
  assert(target);
  assert(targetNotificationChannelFlags);
  const activeChannels: string[] = [];
  if (
    isFlagEnabled(targetNotificationChannelFlags, NotificationChannel.Email) &&
    notificationChannels?.[NotificationChannel.Email]
  ) {
    activeChannels.push("Email");
  }
  if (
    isFlagEnabled(targetNotificationChannelFlags, NotificationChannel.SMS) &&
    notificationChannels?.[NotificationChannel.SMS]
  ) {
    activeChannels.push("SMS");
  }
  if (
    isFlagEnabled(
      targetNotificationChannelFlags,
      NotificationChannel.Discord
    ) &&
    notificationChannels?.[NotificationChannel.Discord]
  ) {
    activeChannels.push("Discord");
  }
  if (
    isFlagEnabled(
      targetNotificationChannelFlags,
      NotificationChannel.Telegram
    ) &&
    notificationChannels?.[NotificationChannel.Telegram]
  ) {
    activeChannels.push("Telegram");
  }
  return (
    <div className="flex flex-row items-center justify-between w-full">
      <div className="flex flex-col gap-2">
        <BettorDisplay bettor={target.bettor} disableLastPicks noCard />
        <p className="text-body-regular text-secondary-400">
          {activeChannels.join(", ")}
        </p>
      </div>
      <div className="flex flex-row items-center gap-4">
        <Button
          size="xs"
          color="tertiary"
          onClick={() => setManagingTarget(true)}
        >
          Manage
        </Button>
        <Button
          size="xs"
          color="tertiary"
          variant="circle"
          onClick={() => removeHighVoltageTarget(i)}
        >
          <Icon name="bin" size="sm" />
        </Button>
        <ManageTarget />
      </div>
    </div>
  );
}

export function ChannelList({disabled}: {disabled: boolean}) {
  const { getToken } = useAuth();
  const {
    type,
    target,
    targetNotificationChannelFlags,
    setTargetNotificationChannelFlags,
    notificationChannels,
    setNotificationChannels,
    connectingAccount,
    setConnectingAccount,
  } = useBettorRelationshipActions();
  const [loading, setLoading] = React.useState<boolean>(false);

  const handleDisconnectAccount = async (
    c: NotificationChannelModel
  ): Promise<void> => {
    assert(notificationChannels !== null);
    setLoading(true);
    try {
      const token = await getToken();
      const result: Result<string> = await mlClient.post(
        `/bettors/notifications/disconnect`,
        {
          notification_channel: c.type,
        },
        token
      );
      if (!result.ok) {
        throw new Error(result.message);
      }
      const { [c.type]: _, ...newNotificationChannels } = notificationChannels;
      setNotificationChannels(newNotificationChannels);
    } catch (err) {
      console.error("Error in handleDisconnectAccount", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckedChange = async (
    b: boolean,
    flag?: NotificationChannel
  ) => {
    assert(target !== undefined);
    assert(targetNotificationChannelFlags !== null);
    try {
      if (flag === undefined) {
        const token = await getToken();
        const result = await mlClient.post(
          "/bettors",
          {
            type: BettorActionType.RelationshipNotificationChannelDisableAll,
            data: {
              notification_channel_flag: 0,
              target: target.bettor.id,
            },
          },
          token
        );
        if (!result.ok) {
          throw new Error(result.message);
        }
        setTargetNotificationChannelFlags(0);
      } else {
        if (b) {
          const token = await getToken();
          const result = await mlClient.post(
            "/bettors",
            {
              type: BettorActionType.RelationshipNotificationChannelEnable,
              data: {
                notification_channel_flag: flag,
                target: target.bettor.id,
              },
            },
            token
          );
          if (!result.ok) {
            throw new Error(result.message);
          }
          setTargetNotificationChannelFlags(
            enableFlag(targetNotificationChannelFlags, flag)
          );
        } else {
          const token = await getToken();
          const result = await mlClient.post(
            "/bettors",
            {
              type: BettorActionType.RelationshipNotificationChannelDisable,
              data: {
                notification_channel_flag: flag,
                target: target.bettor.id,
              },
            },
            token
          );
          if (!result.ok) {
            throw new Error(result.message);
          }
          setTargetNotificationChannelFlags(
            disableFlag(targetNotificationChannelFlags, flag)
          );
        }
      }
    } catch (err) {
      console.error("Error in handleCheckedChange", err);
    }
  };

  const renderRow = (channel: NotificationChannelModel): React.ReactNode => {
    return (
      <div className="flex flex-row gap-3 md:gap-4 items-center justify-between w-full pr-6">
        <div className="flex gap-2 md:gap-4">
          <div className="flex flex-col w-9 md:w-12 min-w-[36px] md:min-w-[48px] h-9 md:h-12 items-center justify-center rounded-full bg-secondary-300">
            <Icon
              name={channel.icon}
              size="lg"
              className="fill-primary text-primary w-5 h-5"
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <h4 className="text-headline-medium text-secondary-300">
              {channel.name}
            </h4>
            <p className="text-body-regular text-secondary-400 max-w-[240px]">
              {channel.description}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          {notificationChannels?.[channel.type] ? (
            <>
              {targetNotificationChannelFlags !== null ? (
                <Switch
                disabled={disabled}
                  id={`switch-${channel.type}`}
                  checked={isFlagEnabled(
                    targetNotificationChannelFlags,
                    channel.type
                  )}
                  onCheckedChange={(b) => handleCheckedChange(b, channel.type)}
                />
              ) : (
                <>
                  <div className="flex flex-col w-5 min-w-[20px] h-5 items-center justify-center rounded-full bg-green-700/20">
                    <Icon
                      name="checkmark-sm"
                      size="md"
                      className="fill-success-300 md:w-[18px] md:h-[18px]"
                    />
                  </div>
                  <p className="text-body-regular text-secondary-300 mr-1">
                    Connected
                  </p>
                  <AlertDialog
                    action={{
                      label: "Yes, Disconnect",
                      onClick: async () => handleDisconnectAccount(channel),
                    }}
                    danger
                    message="Are you sure you want to disconnect this account?"
                    title="Disconnect Account"
                    trigger={
                      <Button
                        color="tertiary"
                        size="xs"
                        variant="standard"
                        className="w-fit bg-surface-600"
                      >
                        <Icon
                          name="bin"
                          size="xs"
                          className="fill-secondary-300 w-[16px] h-[16px]"
                        />
                      </Button>
                    }
                  />
                </>
              )}
            </>
          ) : (
            <>
              <Button
                color="tertiary"
                size="xs"
                variant="standard"
                disabled={disabled}
                onClick={() => setConnectingAccount(channel)}
              >
                Connect
              </Button>
              <Modal open={connectingAccount !== null}>
                <Modal.Header
                  onClose={() => setConnectingAccount(null)}
                  title={
                    <div className="flex flex-row items-center gap-4">
                      <span>Connect Account - {connectingAccount?.name}</span>
                    </div>
                  }
                />
                <div className="flex flex-col p-4 px-6 pt-0 md:px-12 md:pt-6 md:pb-10 w-full gap-6 md:gap-8 md:w-[760px]">
                  {connectingAccount?.type === NotificationChannel.Email ? (
                    <ConnectEmail />
                  ) : connectingAccount?.type ===
                    NotificationChannel.Telegram ? (
                    <ConnectTelegram />
                  ) : connectingAccount?.type === NotificationChannel.SMS ? (
                    <ConnectSms />
                  ) : connectingAccount?.type ===
                    NotificationChannel.Discord ? (
                    <ConnectDiscord />
                  ) : undefined}
                </div>
              </Modal>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {NOTIFICATION_CHANNELS.map((c) => (
        <>{renderRow(c)}</>
      ))}
      {/* {type === "target-profile" &&
        target &&
        targetNotificationChannelFlags && (
          <div className="flex flex-row p-4 md:p-6 rounded-xl gap-3 md:gap-4 items-center justify-between bg-surface-600">
            <h4 className="text-headline-medium text-secondary-300">
              Turn off all alerts for {target.bettor.displayName}
            </h4>
            <Switch
              id="switchAll"
              checked={!hasAnyFlag(targetNotificationChannelFlags)}
              onCheckedChange={(b) => handleCheckedChange(b)}
            />
          </div>
        )} */}
    </>
  );
}

function ConnectTelegram() {
  const { getToken } = useAuth();
  const [deepLink, setDeepLink] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    const getDeepLink = async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const result: Result<string> = await mlClient.get(
          `/bettors/notifications/connect/telegram`,
          token
        );
        if (!result.ok) {
          throw new Error(result.message);
        }
        setDeepLink(result.data);
      } catch (err) {
        console.error("Error in handleConnectAccount", err);
      } finally {
        setLoading(false);
      }
    };
    getDeepLink();
  }, []);
  if (loading || !deepLink) {
    return (
      <div className="flex justify-center items-center w-full h-full pb-12">
        <LoadingCircle />
      </div>
    );
  }
  return (
    <>
      <div className="flex flex-col gap-4 items-center">
        <div className="text-body-regular text-secondary-400">
          We're about to open Telegram so you can subscribe to our bot.
        </div>
        <div className="flex flex-col gap-2">
          <div className="text-body-sm">1. Click the button below.</div>
          <div className="text-body-sm">
            2. Click <strong>Start</strong> or send `/start` in the chat.
          </div>
          <div className="text-body-sm">3. Come back here to finish.</div>
        </div>
        <div className="flex justify-center">
          <a
            href={deepLink}
            target="_blank"
            className="btn btn-md btn-primary btn-filled-primary"
          >
            Open Telegram
          </a>
        </div>
      </div>
    </>
  );
}

function ConnectEmail() {
  const { getToken } = useAuth();
  const {
    setConnectingAccount,
    notificationChannels,
    setNotificationChannels,
  } = useBettorRelationshipActions();

  const [step, setStep] = React.useState<"enter" | "verify" | "done">("enter");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [email, setEmail] = React.useState("");
  const [code, setCode] = React.useState("");

  const handleSendCode = async () => {
    setError(null);
    assert(email, "Email is required");
    setLoading(true);
    try {
      const token = await getToken();
      const result: Result<string> = await mlClient.post(
        "/bettors/notifications/email/send-code",
        { email },
        token
      );
      if (!result.ok) throw new Error(result.message);
      setStep("verify");
    } catch (err: any) {
      console.error("Error sending code:", err);
      setError(err.message || "Failed to send code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError(null);
    assert(email && code, "Email and code are required");
    setLoading(true);

    try {
      const token = await getToken();
      const result: Result<string> = await mlClient.post(
        "/bettors/notifications/email/verify-code",
        { email, code },
        token
      );
      if (!result.ok) throw new Error(result.message);
      const newNotificationChannels = {
        [NotificationChannel.Email]: email,
        ...notificationChannels,
      };
      setNotificationChannels(newNotificationChannels);
      setStep("done");
    } catch (err: any) {
      console.error("Error verifying code:", err);
      setError(err.message || "Invalid or expired code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <LoadingCircle />
      </div>
    );
  }

  return (
    <>
      {step === "enter" && (
        <div className="flex flex-col gap-2 justify-center items-center">
          <h2 className="text-xl font-semibold mb-4">Connect Your Email</h2>
          <p className="mb-4 text-gray-600">
            Enter your email below and we'll send you a one-time code.
          </p>
          <Form>
            <Form.Field name="email" className="w-full mb-4">
              <Form.Label>Email Address</Form.Label>

              <Form.Input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="!w-full !max-w-[400px]"
              />
              <Form.Message match="valueMissing">
                Please enter an email address.
              </Form.Message>
              <Form.Message match="typeMismatch">
                Please enter a valid email address.
              </Form.Message>
            </Form.Field>
          </Form>
          <Button onClick={handleSendCode} disabled={!email}>
            Send Verification Code
          </Button>
        </div>
      )}

      {step === "verify" && (
        <div className="flex flex-col gap-2 justify-center items-center">
          <h2 className="text-xl font-semibold mb-4">
            Enter Verification Code
          </h2>
          <p className="mb-4 text-gray-600">
            We sent a code to <strong>{email}</strong>.{" "}
            <button
              onClick={() => setStep("enter")}
              className="underline text-sm text-blue-600"
            >
              Change email
            </button>
          </p>
          <Form>
            <Form.Field name="code">
              <Form.Label>Verification Code</Form.Label>
              <Form.Input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                required
                maxLength={6}
                placeholder="123456"
                onChange={(e) => setCode(e.target.value)}
                value={code}
              />
              <Form.Message match="valueMissing">Enter the code.</Form.Message>
              <Form.Message match="patternMismatch">
                Code must be exactly 6 digits.
              </Form.Message>
            </Form.Field>
          </Form>
          <Button onClick={handleVerifyCode} disabled={code.length !== 6}>
            Verify Code
          </Button>
        </div>
      )}

      {step === "done" && (
        <div className="text-center flex flex-col gap-4 justify-center items-center">
          <h2 className="text-xl font-semibold mb-2">Email Connected!</h2>
          <p className="text-gray-600">
            You'll now receive Moneyline alerts at <strong>{email}</strong>.
          </p>
          <Button onClick={() => setConnectingAccount(null)}>Done</Button>
        </div>
      )}
    </>
  );
}

function ConnectSms() {
  const { getToken } = useAuth();
  const { setConnectingAccount } = useBettorRelationshipActions();

  const [step, setStep] = React.useState<"enter" | "verify" | "done">("enter");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [phone, setPhone] = React.useState("");
  const [code, setCode] = React.useState("");

  const handleSendCode = async () => {
    setError(null);
    assert(phone, "Phone is required");
    setLoading(true);
    try {
      const token = await getToken();
      const result: Result<string> = await mlClient.post(
        "/bettors/notifications/sms/send-code",
        { phone },
        token
      );
      if (!result.ok) throw new Error(result.message);
      setStep("verify");
    } catch (err: any) {
      console.error("Error sending code:", err);
      setError(err.message || "Failed to send code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError(null);
    assert(phone && code, "Email and code are required");
    setLoading(true);

    try {
      const token = await getToken();
      const result: Result<string> = await mlClient.post(
        "/bettors/notifications/sms/verify-code",
        { phone, code },
        token
      );
      if (!result.ok) throw new Error(result.message);
      setStep("done");
    } catch (err: any) {
      console.error("Error verifying code:", err);
      setError(err.message || "Invalid or expired code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <LoadingCircle />
      </div>
    );
  }

  return (
    <>
      {step === "enter" && (
        <div className="flex flex-col gap-2 justify-center items-center">
          <h2 className="text-xl font-semibold mb-4">Connect Your Phone</h2>
          <p className="mb-4 text-gray-600">
            Enter your phone number below and we'll send you a one-time code.
          </p>
          <Form>
            <Form.Field name="email" className="w-full mb-4">
              <Form.Label>Phone</Form.Label>

              <Form.Input
                type="phone"
                required
                placeholder="+1-(777)-777-7777"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="!w-full !max-w-[400px]"
              />
              <Form.Message match="valueMissing">
                Please enter a phone number.
              </Form.Message>
              <Form.Message match="typeMismatch">
                Please enter a valid phone number.
              </Form.Message>
            </Form.Field>
          </Form>
          <Button onClick={handleSendCode} disabled={!phone}>
            Send Verification Code
          </Button>
        </div>
      )}

      {step === "verify" && (
        <div className="flex flex-col gap-2 justify-center items-center">
          <h2 className="text-xl font-semibold mb-4">
            Enter Verification Code
          </h2>
          <p className="mb-4 text-gray-600">
            We sent a code to <strong>{phone}</strong>.{" "}
            <button
              onClick={() => setStep("enter")}
              className="underline text-sm text-blue-600"
            >
              Change phone
            </button>
          </p>
          <Form>
            <Form.Field name="code">
              <Form.Label>Verification Code</Form.Label>
              <Form.Input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                required
                maxLength={6}
                placeholder="123456"
                onChange={(e) => setCode(e.target.value)}
                value={code}
              />
              <Form.Message match="valueMissing">Enter the code.</Form.Message>
              <Form.Message match="patternMismatch">
                Code must be exactly 6 digits.
              </Form.Message>
            </Form.Field>
          </Form>
          <Button
            onClick={handleVerifyCode}
            disabled={code.length !== 6}
            className="btn btn-md btn-filled-primary"
          >
            Verify Code
          </Button>
        </div>
      )}

      {step === "done" && (
        <div className="text-center flex flex-col gap-4 justify-center items-center">
          <h2 className="text-xl font-semibold mb-2">Phone Connected!</h2>
          <p className="text-gray-600">
            You'll now receive Moneyline alerts at <strong>{phone}</strong>.
          </p>
          <Button onClick={() => setConnectingAccount(null)}>Done</Button>
        </div>
      )}
    </>
  );
}

function ConnectDiscord() {
  const { getToken } = useAuth();
  const { setConnectingAccount } = useBettorRelationshipActions();

  const [otp, setOtp] = React.useState<string | null>(null);
  const [inviteLink, setInviteLink] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    if (otp) {
      await navigator.clipboard.writeText(`!subscribe ${otp}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  React.useEffect(() => {
    const fetchOtp = async () => {
      try {
        const token = await getToken();
        const result: Result<{ discord_invite_link: string; otp: string }> =
          await mlClient.get(
            "/bettors/notifications/discord/request-code",
            token
          );
        if (!result.ok) throw new Error(result.message);
        if (result.data) {
          setOtp(result.data.otp);
          setInviteLink(result.data.discord_invite_link);
        }
      } catch (err: any) {
        console.error("Failed to fetch OTP:", err);
        setError("Failed to load Discord connection. Try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchOtp();
  }, [getToken]);

  if (loading || !inviteLink || !otp) {
    return (
      <div className="flex justify-center items-center h-48">
        <LoadingCircle />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="flex flex-col gap-4 items-center text-center">
      <h2 className="text-xl font-semibold">Connect Your Discord</h2>
      <p className="text-gray-600 max-w-md">
        To receive Moneyline alerts via Discord, follow these steps:
        <ol className="list-decimal list-inside mt-2 text-left">
          <li>Join the official Moneyline Discord server.</li>
          <li>
            After joining the server, locate the{" "}
            <strong>MoneylineNotifyBot</strong> in the member list and click
            their name to start a direct message. Then paste the code below.
          </li>
          <li onClick={handleCopy} className="cursor-pointer">
            <code className="bg-gray-200 px-1 py-0.5 rounded w-full">
              !subscribe {otp}
            </code>
            {copied && (
              <span className="ml-2 text-success-300 text-sm">Copied!</span>
            )}
          </li>
        </ol>
      </p>

      <p className="text-sm font-medium">This code expires in 5 minutes.</p>

      <div className="flex flex-col gap-2">
        <a
          href={inviteLink}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-md btn-filled-primary"
        >
          Join Discord Server
        </a>
      </div>

      <div className="flex flex-row justify-end items-center w-full">
        <Button onClick={() => setConnectingAccount(null)}>Done</Button>
      </div>
    </div>
  );
}

export function FollowActions() {
  const {
    target,
    setManagingTarget,
    targetNotificationChannelFlags,
    setTargetNotificationChannelFlags,
    highVoltage,
    setHighVoltage
  } = useBettorRelationshipActions();
  assert(target);
  const { bettor } = useBettor();
  const { isSignedIn, getToken } = useAuth();
  const { setAuth } = useContext(AuthContext);
  const { getCooldownState, startCooldown } = useCooldown();
  const cooldownKey = `bettor_action:${target.bettor.id}`;
  const cooldownState = getCooldownState(cooldownKey);
  const [following, setFollowing] = React.useState<boolean>(target.following);
  const [attempted, setAttempted] = React.useState<boolean>(false);
  const disabled =
    cooldownState.isActive && cooldownState.countdown > 0 && attempted;

  const handleBettorRelationshipAction = async (
    action: BettorActionType
  ): Promise<void> => {
    if (!isSignedIn) {
      setAuth("register");
      return;
    }
    if (cooldownState.isActive) {
      setAttempted(true);
      return;
    }
    try {
      const token = await getToken();
      const mlAction: MoneylineAction2<BettorActionType, BettorActionPayload> =
        {
          type: action,
          data: { target: target.bettor.id },
        };
      const response = await mlClient.post("bettors", mlAction, token);
      if (!response.ok) {
        throw new Error(response.message);
      }
      switch (action) {
        case BettorActionType.Follow:
          setFollowing(true);
          setHighVoltage(false);
          setTargetNotificationChannelFlags(DEFAULT_SMALL_INT_FLAGS);
          break;
        case BettorActionType.Unfollow:
          setFollowing(false);
          break;
        case BettorActionType.HighVoltageNotificationEnable:
          setHighVoltage(true);
          setManagingTarget(true);
          break;
        case BettorActionType.HighVoltageNotificationDisable:
          setHighVoltage(false);
          setManagingTarget(false);
          break;
        default:
          const message = `Invalid bettor relationship action: ${action}`;
          console.error(message);
      }
      setAttempted(false);
      startCooldown(cooldownKey, 5000);
    } catch (err: any) {
      console.error("Error in handleBettorRelationshipAction", err);
    }
  };

  if (target.disabled) {
    return null;
  }
  return (
    <>
      <div className="flex gap-2">
        
        <div>
          {following ? (
            <Button
              color="tertiary"
              size="xs"
              className="py-2 min-w-[103px] !justify-center"
              disabled={disabled}
              onClick={() =>
                handleBettorRelationshipAction(BettorActionType.Unfollow)
              }
            >
              {disabled ? (
                <div className="flex items-center gap-2 justify-center w-full">
                  <span className="block">{cooldownState.countdown}</span>
                  <Icon
                    name="clock"
                    size="xs"
                    className="fill-secondary-300 hidden md:block"
                  />
                </div>
              ) : (
                <>
                  <span>Following</span>
                  <Icon name="checkmark-sm" size="sm" />
                </>
              )}
            </Button>
          ) : (
            <Button
              size="xs"
              className="py-2 min-w-[103px] !justify-center"
              onClick={() =>
                handleBettorRelationshipAction(BettorActionType.Follow)
              }
              disabled={disabled}
            >
              {disabled ? (
                <div className="flex items-center gap-2 justify-center w-full">
                  <span className="block">{cooldownState.countdown}</span>
                  <Icon
                    name="clock"
                    size="xs"
                    className="fill-secondary-300 hidden md:block"
                  />
                </div>
              ) : (
                <span>Follow</span>
              )}
            </Button>
          )}
        </div>
        {target.shareable && (
          <div>
            <Button
              href={`${target.shareable}`}
              size="xs"
              className="py-2 min-w-[103px]"
            >
              <Icon name="share" size="sm" />
            </Button>
          </div>
        )}
        
      </div>
    </>
  );
}

function OtpInput({
  length = 6,
  onChange,
}: {
  length?: number;
  onChange: (code: string) => void;
}) {
  const [values, setValues] = React.useState<string[]>(Array(length).fill(""));
  const inputs = React.useRef<HTMLInputElement[]>([]);

  const handleChange = (idx: number, val: string) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...values];
    next[idx] = val;
    setValues(next);
    onChange(next.join(""));

    if (val && idx < length - 1) {
      inputs.current[idx + 1].focus();
    }
  };

  return (
    <div className="flex gap-2">
      {values.map((v, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el!)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={v}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !values[i] && i > 0) {
              inputs.current[i - 1].focus();
            }
          }}
          className="w-10 h-10 text-center border rounded"
        />
      ))}
    </div>
  );
}


// {following && targetNotificationChannelFlags && (
//   <div>
//     <Button
//       color={highVoltage ? "primary" : "tertiary"}
//       size="xs"
//       className="p-2"
//       onClick={() => {
//         setManagingTarget(true);
//         // if (instantNotifications) {
//         //   handleBettorRelationshipAction(
//         //     BettorActionType.HighVoltageNotificationDisable
//         //   );
//         // } else {
//         //   handleBettorRelationshipAction(
//         //     BettorActionType.HighVoltageNotificationEnable
//         //   );
//         // }
//       }}
//     >
//       <Icon name="flash" size="sm" />
//     </Button>
//     <ManageTarget />
//   </div>
// )}


// {bettor?.details.is_admin && target.bettor.displayName && (
//   <Button
//   href={`/dashboard/users?search=${target.bettor.displayName.toLowerCase()}`}
//   size="xs"
//   className="py-2"
// >
//   <Icon name="cog" size="sm" />
// </Button>

// )}